import type {
  ReferrerSummary,
  ReferrerType,
} from "@redotech/admin-sdk/rpc/schema/referrers/shared";
import { useRequiredContext } from "@redotech/react-util/context";
import MailIcon from "@redotech/redo-icons/mail-02.svg";
import { toast } from "@redotech/redo-web/alert";
import { RedoButtonTabGroup } from "@redotech/redo-web/arbiter-components/checkbox/redo-button-tab-group";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import classNames from "classnames";
import { memo, useCallback, useEffect, useMemo, useState } from "react";

import { RedoAdminRpcClientContext } from "../app/redo-admin-rpc-client-provider";
import * as modalStyles from "../onboardings/workspace/v2-product-configurations.module.css";
import { ReferralCodeInput } from "./components/referral-code-input";
import { mapReferrerErrorToFieldError } from "./lib/map-referrer-error-to-field-errors";

interface CreateReferrerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (referrer: ReferrerSummary) => void;
}

const REFERRER_TYPE_OPTIONS: { value: ReferrerType; el: string }[] = [
  { value: "individual", el: "Individual" },
  { value: "app", el: "App" },
  { value: "agency", el: "Agency" },
];

// Reuse the shared image-header modal treatment until referrers has its own asset.
const REFERRER_MODAL_IMAGE_URL =
  "https://redoapi-dev.s3.us-east-1.amazonaws.com/redo/static/onboarding-v2/onboarding-v2-returns.webp";

export const CreateReferrerModal = memo(function CreateReferrerModal({
  isOpen,
  onClose,
  onCreated,
}: CreateReferrerModalProps) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [referralCodeValid, setReferralCodeValid] = useState(true);
  const [referrerType, setReferrerType] = useState<ReferrerType>("individual");
  const [sendMagicLinkImmediately, setSendMagicLinkImmediately] =
    useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [emailError, setEmailError] = useState<string | undefined>();
  const [referralCodeServerError, setReferralCodeServerError] = useState<
    string | undefined
  >();
  const [statusError, setStatusError] = useState<string | undefined>();
  const [formError, setFormError] = useState<string | undefined>();

  // Pre-warm the profanity matcher (decision 13A) so the first keystroke
  // in the referral-code field doesn't pause while obscenity loads.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    void (async () => {
      const { containsProfanity } =
        await import("@redotech/redo-referrals-core/referrer-account/referral-code");
      void containsProfanity("warmup");
    })();
  }, [isOpen]);

  const reset = useCallback(() => {
    setEmail("");
    setName("");
    setReferralCode("");
    setReferralCodeValid(true);
    setReferrerType("individual");
    setSendMagicLinkImmediately(true);
    setSubmitting(false);
    setEmailError(undefined);
    setReferralCodeServerError(undefined);
    setStatusError(undefined);
    setFormError(undefined);
  }, []);

  const handleClose = useCallback(() => {
    if (submitting) {
      return;
    }
    reset();
    onClose();
  }, [submitting, reset, onClose]);

  const checkAvailable = useCallback(
    (code: string, opts: { signal: AbortSignal }) =>
      rpcClient.checkReferralCodeAvailable({ code }, opts),
    [rpcClient],
  );

  const submit = useCallback(async () => {
    setEmailError(undefined);
    setReferralCodeServerError(undefined);
    setStatusError(undefined);
    setFormError(undefined);
    setSubmitting(true);

    try {
      const result = await rpcClient.createReferrer({
        email: email.trim(),
        name: name.trim(),
        referralCode:
          referralCode.trim().length > 0
            ? referralCode.trim().toUpperCase()
            : undefined,
        referrerType,
        sendMagicLinkImmediately,
      });

      if (result.magicLinkSent) {
        toast("Referrer created and invited via email.", {
          variant: "success",
        });
      } else if (sendMagicLinkImmediately) {
        toast(
          "Referrer created. Email delivery failed — they will need a resend.",
          { variant: "warning" },
        );
      } else {
        toast(
          "Referrer created. Use Resend on the detail page to invite them.",
          { variant: "success" },
        );
      }

      onCreated(result.referrer);
      reset();
      onClose();
    } catch (err) {
      const mapping = mapReferrerErrorToFieldError(err);
      // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
      switch (mapping?.field) {
        case "email":
          setEmailError(mapping.message);
          break;
        case "referralCode":
          setReferralCodeServerError(mapping.message);
          break;
        case "status":
          setStatusError(mapping.message);
          break;
        default:
          setFormError(
            mapping?.message ??
              (err instanceof Error
                ? err.message
                : "Failed to create referrer. Please try again."),
          );
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    rpcClient,
    email,
    name,
    referralCode,
    referrerType,
    sendMagicLinkImmediately,
    onCreated,
    reset,
    onClose,
  ]);

  const submitDisabled =
    submitting ||
    email.trim().length === 0 ||
    name.trim().length === 0 ||
    !referralCodeValid;

  const subtitle = useMemo(() => {
    if (sendMagicLinkImmediately) {
      return "Create the account and email them a magic-link sign-in.";
    }
    return "Create the account. You can email them later from the detail page.";
  }, [sendMagicLinkImmediately]);

  if (!isOpen) {
    return null;
  }

  return (
    <RedoModal
      hideFooter
      hideHeader
      isOpen={isOpen}
      modalSize="sm"
      muiClassName={modalStyles.exitModalRoot}
      noPadding
      onModalCloseRequested={handleClose}
      title="Create referrer"
    >
      <div className={modalStyles.modalImageWrapper}>
        <img
          alt=""
          className={modalStyles.modalHeaderImage}
          src={REFERRER_MODAL_IMAGE_URL}
        />
        <div className={modalStyles.modalImageOverlay} />
        <div className={modalStyles.modalImageLabel}>
          <Text
            as="span"
            className={modalStyles.modalImageTitle}
            lineHeight="1"
          >
            New referrer
          </Text>
          <Text as="p" className={modalStyles.modalImageSubheading}>
            {subtitle}
          </Text>
        </div>
      </div>

      <div className={modalStyles.modalContentBody}>
        <Flex dir="column" gap="md">
          <RedoTextInput
            errorText={emailError}
            label="Email"
            placeholder="referrer@example.com"
            setValue={setEmail}
            value={email}
          />
          <RedoTextInput
            label="Name"
            placeholder="Referrer display name"
            setValue={setName}
            value={name}
          />
          <ReferralCodeInput
            checkAvailable={checkAvailable}
            nameForAutoGen={name}
            onChange={setReferralCode}
            onValidityChange={setReferralCodeValid}
            value={referralCode}
          />
          {referralCodeServerError ? (
            <Text fontSize="xs" textColor="error">
              {referralCodeServerError}
            </Text>
          ) : null}

          <Flex align="flex-start" dir="column" gap="xs">
            <Text fontSize="xs" fontWeight="medium" textColor="primary">
              Referrer type
            </Text>
            <Flex
              align="center"
              aria-label="Referrer type"
              gap="none"
              role="group"
              shrink={0}
              w="fit-content"
            >
              <RedoButtonTabGroup
                fullWidth={false}
                onChange={setReferrerType}
                options={REFERRER_TYPE_OPTIONS}
                size="sm"
                value={referrerType}
              />
            </Flex>
          </Flex>

          <label
            className={modalStyles.fieldCard}
            htmlFor="create-referrer-send-magic-link"
          >
            <div className={modalStyles.fieldLabelBlock}>
              <div className={modalStyles.fieldIconWrap}>
                <MailIcon className={modalStyles.fieldIcon} />
              </div>
              <Text as="span" className={modalStyles.fieldTitle}>
                Send magic link immediately
              </Text>
            </div>
            <div className={modalStyles.fieldControl}>
              {/* design-allow: checkbox styled by the shared onboarding modal */}
              <input
                checked={sendMagicLinkImmediately}
                className={modalStyles.checkbox}
                id="create-referrer-send-magic-link"
                onChange={(e) => setSendMagicLinkImmediately(e.target.checked)}
                type="checkbox"
              />
            </div>
          </label>

          {statusError ? (
            <Text fontSize="xs" textColor="error">
              {statusError}
            </Text>
          ) : null}
          {formError ? (
            <Text fontSize="xs" textColor="error">
              {formError}
            </Text>
          ) : null}
        </Flex>

        <div className={modalStyles.actionsRow}>
          {/* design-allow: cancel action uses the shared onboarding modal button styles */}
          <button
            className={modalStyles.exitModalActionBtn}
            disabled={submitting}
            onClick={handleClose}
            type="button"
          >
            Cancel
          </button>
          {/* design-allow: submit action uses the shared onboarding modal button styles */}
          <button
            className={classNames(
              modalStyles.exitModalActionBtn,
              modalStyles.exitModalActionBtnPrimary,
              modalStyles.primaryActionBtn,
            )}
            disabled={submitDisabled}
            onClick={() => void submit()}
            type="button"
          >
            <Text
              as="span"
              className={classNames(
                modalStyles.primaryActionLabel,
                submitting && modalStyles.primaryActionLabelHidden,
              )}
            >
              {submitting ? "Creating…" : "Create referrer"}
            </Text>
            {submitting && (
              <span
                aria-label="Creating"
                className={modalStyles.primaryActionSpinner}
                role="status"
              />
            )}
          </button>
        </div>
      </div>
    </RedoModal>
  );
});
